import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock, CheckCircle, XCircle, Search, AlertTriangle, Loader2 } from 'lucide-react';
import { useMyDisputes, DisputeStatus } from '../../../hooks/useDisputes';

const STATUS_CONFIG: Record<DisputeStatus, { label: string; color: string; icon: React.ElementType }> = {
  PENDING_OCR: { label: 'Processando', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
  PENDING_VERIFICATION: { label: 'Verificando', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Search },
  VERIFIED: { label: 'Verificado', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  UNVERIFIED: { label: 'Não Verificado', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', icon: AlertTriangle },
  OCR_FAILED: { label: 'Erro OCR', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', icon: AlertTriangle },
  DUPLICATE_IMAGE: { label: 'Duplicado', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  DUPLICATE_E2E: { label: 'Duplicado', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  PENDING_REVIEW: { label: 'Em Análise', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: Clock },
  PENDING_SECOND_APPROVAL: { label: 'Aguardando 2ª Aprovação', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: Clock },
  APPROVED: { label: 'Aprovado', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  REJECTED: { label: 'Rejeitado', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  CANCELLED: { label: 'Cancelado', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400', icon: XCircle },
  ALREADY_CREDITED: { label: 'Já Creditado', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
};

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function relativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  return date.toLocaleDateString('pt-BR');
}

export function DisputeList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useMyDisputes({ page, limit: 10 });

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Minhas Contestações</h1>
        <button
          onClick={() => navigate('/banking/disputes/new')}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nova Contestação</span>
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!data?.data || data.data.length === 0) && (
        <div className="text-center py-12 space-y-3">
          <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto" />
          <p className="text-gray-500 dark:text-gray-400">Nenhuma contestação encontrada</p>
          <button
            onClick={() => navigate('/banking/disputes/new')}
            className="text-primary-500 hover:text-primary-600 font-medium text-sm"
          >
            Criar primeira contestação
          </button>
        </div>
      )}

      {/* Cards (mobile) / Table (desktop) */}
      {data?.data && data.data.length > 0 && (
        <>
          {/* Mobile Cards */}
          <div className="space-y-3 md:hidden">
            {data.data.map((dispute) => {
              const statusCfg = STATUS_CONFIG[dispute.status];
              const StatusIcon = statusCfg.icon;
              return (
                <button
                  key={dispute.id}
                  onClick={() => navigate(`/banking/disputes/${dispute.id}`)}
                  className="w-full text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(dispute.claimedAmountCents)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {dispute.claimedType === 'DEPOSIT' ? 'Depósito' : 'Transferência'} • {relativeDate(dispute.createdAt)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusCfg.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Valor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                {data.data.map((dispute) => {
                  const statusCfg = STATUS_CONFIG[dispute.status];
                  const StatusIcon = statusCfg.icon;
                  return (
                    <tr
                      key={dispute.id}
                      onClick={() => navigate(`/banking/disputes/${dispute.id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(dispute.claimedAmountCents)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {dispute.claimedType === 'DEPOSIT' ? 'Depósito' : 'Transferência'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {relativeDate(dispute.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusCfg.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {page} de {data.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                Próximo
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DisputeList;
